import type { FieldDefinition, PersonDetail, ShredReasonCode } from '@sentra/contracts';
import { ageOnDate, roleHasPermission } from '@sentra/contracts';
import { useQuery } from '@tanstack/react-query';
import { Flame, Pencil } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { FailureNote } from '@/api/failure';
import { useApiMutation } from '@/api/mutations';
import { queryKeys, usePersonCredentials } from '@/api/queries';
import { apiRoutes } from '@/api/routes';
import { notePersonWrite } from '@/api/write-clock';
import { Alert, AlertDescription, AlertTitle } from '@/design/components/alert';
import { Button } from '@/design/components/button';
import { useApiClient, useSession } from '@/features/auth/session';
import { sectionHeading } from '@/features/persons/form/section-heading';
import { groupBySection } from '@/features/persons/form/values';
import { PersonPhotographs } from '@/features/persons/images/photographs';
import { EraseDialog } from '@/features/persons/record/erase-dialog';
import { FieldValue } from '@/features/persons/record/field-value';
import { ManualMovement } from '@/features/persons/record/manual-movement';
import { useLocale } from '@/i18n';
import { useFormatters } from '@/i18n/use-format';

/**
 * One person's whole record: what is stored, who they are at the gate, and the two acts that can
 * only be reached from here.
 *
 * **Reaching this screen has already written an audit row** (AD-53). `person.get` records a
 * `person.view_full` on every call, into a table that admits no DELETE and that AD-37's
 * exfiltration threshold counts — which is why the register asks for an acknowledgment first, why
 * the detail query never refetches on window focus, and why this screen shows everything rather
 * than making somebody open it twice.
 *
 * **The fields are rendered FROM THE REGISTRY** (AD-16), in its own order, with its own bilingual
 * labels. There is no field list here: an administrator adds a field and it appears on every
 * record, and a field they retire stops being offered while its stored values stay readable — which
 * is why a retired definition with a value is still rendered, marked as retired.
 *
 * **It shows, and it does not change.** The photographs, the passes and the situation are all here
 * to be read; adding a photograph, binding or replacing a band and moving the record between
 * `active`, `blocked` and `archived` are changes to a person, and every change to a person is made
 * on the amendment screen this one's own control opens. That split is what stops a destructive
 * control sitting on the screen an operator has open with somebody in front of them.
 *
 * **Two writes stay, and both are deliberate.** Recording an entry or an exit for somebody who is
 * not carrying their band is what an operator does WHILE looking at a record, with the person at
 * the desk; sending them through an edit screen to do it would be sending them away from the person
 * they are admitting. And the erasure stays, because it is a record-level act rather than a change
 * to what a record says — it is gated on `person:shred`, demands the record's own short reference
 * typed out and a reason, and is reached from the register's row menu as well.
 *
 * **A movement reached by search is recorded here too**, rather than by navigating to the gate
 * screen with a person id it cannot use: the gate's panel is rendered from a scan's answer, which a
 * person id cannot produce, and the only route that answers by id is the audited full disclosure
 * this screen has already performed.
 */
/** The one core key this screen treats specially, because the age is rendered beside it. */
const BIRTH_DATE_KEY = 'date_of_birth';

export function RecordScreen({
  person,
  definitions,
  openErasure = false,
  onAmend,
  onErased,
}: Readonly<{
  person: PersonDetail;
  definitions: readonly FieldDefinition[];
  /**
   * True when the record was opened BY the erasure row action.
   *
   * The dialog opens on arrival rather than being reached by a second click, because the
   * acknowledgment the operator has already given said what they were about to do — and the typed
   * short reference is the gate, not the number of clicks in front of it.
   */
  openErasure?: boolean;
  onAmend: () => void;
  /** Called once the record is gone, so the caller can leave a screen about nobody. */
  onErased: () => void;
}>): ReactNode {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const format = useFormatters();
  const session = useSession();
  const client = useApiClient();

  const [erasing, setErasing] = useState(openErasure);

  const { credentials } = usePersonCredentials(person.id);
  const revisions = useQuery({
    queryKey: queryKeys.personRevisions(person.id),
    queryFn: () => client.request(apiRoutes.personRevisions, { params: { id: person.id } }),
  });

  const erase = useApiMutation(
    apiRoutes.personShred,
    (reasonCode: ShredReasonCode) => ({
      params: { id: person.id },
      body: { confirmation: person.shortRef, reasonCode },
    }),
    {
      onSuccess: () => {
        // The erased record has to DISAPPEAR from the register, which is the half of this that
        // looking at the answer could never detect: an empty list is not what an erasure produces.
        notePersonWrite();
        setErasing(false);
        onErased();
      },
    },
  );

  const mayErase = session !== null && roleHasPermission(session.user.role, 'person:shred');
  // **The record SHOWS and the amendment CHANGES, and each control asks for its own verb.** Edit is
  // `person:write` and a movement is `presence:submit`; the analyst holds neither and reads this
  // screen in full, so for it the record is exactly what its name says.
  const mayAmend = session !== null && roleHasPermission(session.user.role, 'person:write');
  const mayMove = session !== null && roleHasPermission(session.user.role, 'presence:submit');
  const passes = credentials ?? [];
  const stored = definitions.filter((definition) => person.fields[definition.key] !== undefined);

  /**
   * The age a stored birthday works out to today, or `null` when there is nothing to compute from.
   *
   * `format.calendarDate` cuts today's date in the installation's reporting zone, which is the same
   * boundary every other date on this screen is rendered against — a record read at 00:30 in Madrid
   * must not age somebody by the UTC calendar.
   */
  const ageOf = (value: unknown): number | null =>
    typeof value === 'string' && value.length > 0
      ? ageOnDate(value, format.calendarDate(new Date()))
      : null;

  return (
    <div className="flex flex-col gap-6" data-testid="person-record">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h2 className="text-xl font-semibold tracking-tight">
            {t('person.record.title', { shortRef: person.shortRef })}
          </h2>
          <p className="text-sm text-muted-foreground">
            {t('person.record.summary', {
              status: t(`person.table.status_${person.status}`),
              presence: t(`person.table.presence_${person.presenceState}`),
            })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {mayAmend && (
            <Button type="button" variant="outline" data-testid="record-edit" onClick={onAmend}>
              <Pencil aria-hidden />
              {t('person.record.edit')}
            </Button>
          )}
          {mayErase && (
            <Button
              type="button"
              variant="destructive"
              data-testid="erase-person"
              onClick={() => {
                setErasing(true);
              }}
            >
              <Flame aria-hidden />
              {t('person.record.erase')}
            </Button>
          )}
        </div>
      </div>

      {person.status === 'archived' && (
        <Alert data-testid="reactivate-prompt">
          <AlertTitle>{t('person.record.archivedTitle')}</AlertTitle>
          {/* The body tells the reader to open the amendment, which only a role that may amend has. */}
          {mayAmend && <AlertDescription>{t('person.record.archivedBody')}</AlertDescription>}
        </Alert>
      )}

      {person.completeness.missing.length > 0 && (
        <Alert>
          <AlertTitle>{t('person.record.incompleteTitle')}</AlertTitle>
          <AlertDescription>
            {format.list(
              person.completeness.missing.map(
                (key) => definitions.find((one) => one.key === key)?.label[locale] ?? key,
              ),
            )}
          </AlertDescription>
        </Alert>
      )}

      <section className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold">{t('person.record.fields')}</h3>
        {groupBySection(stored).map((group) => (
          <div
            key={group.section ?? ''}
            className="flex flex-col gap-2"
            data-section={group.section ?? ''}
          >
            <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {sectionHeading(group.section, t)}
            </h4>
            <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[14rem_1fr]">
              {group.fields.map((definition) => (
                <Fragment key={definition.id}>
                  <div className="contents" data-testid="record-field">
                    <dt className="text-sm text-muted-foreground">
                      {definition.label[locale]}
                      {definition.status === 'retired' && ` ${t('person.record.retiredField')}`}
                    </dt>
                    <dd className="text-sm">
                      <FieldValue
                        definition={definition}
                        value={person.fields[definition.key]}
                        locale={locale}
                        format={format}
                      />
                    </dd>
                  </div>
                  {/*
                    The age, beside the birthday it is computed from. Derived rather than stored, so
                    it cannot go stale and cannot disagree with the date above it.
                  */}
                  {definition.key === BIRTH_DATE_KEY &&
                    ageOf(person.fields[definition.key]) !== null && (
                      <div className="contents" data-testid="record-age">
                        <dt className="text-sm text-muted-foreground">{t('person.record.age')}</dt>
                        <dd className="text-sm">{ageOf(person.fields[definition.key])}</dd>
                      </div>
                    )}
                </Fragment>
              ))}
            </dl>
          </div>
        ))}
      </section>

      {person.adhocFields.length > 0 && (
        <section className="flex flex-col gap-2">
          <h3 className="text-sm font-semibold">{t('person.record.adhoc')}</h3>
          <dl className="grid gap-x-4 gap-y-2 sm:grid-cols-[14rem_1fr]">
            {person.adhocFields.map((field) => (
              <div key={field.name} className="contents" data-testid="record-adhoc">
                <dt className="text-sm text-muted-foreground">{field.name}</dt>
                <dd className="text-sm">{field.value}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <PersonPhotographs personId={person.id} />

      {mayMove && <ManualMovement person={person} />}

      {/*
        The passes this person has held, to READ. Binding a band, replacing one and delivering the
        code are acts rather than facts, and they live on the amendment screen with everything else
        that changes a record.
      */}
      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('person.record.credentials')}</h3>
        {passes.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('person.record.noCredential')}</p>
        )}
        <ul className="flex flex-col gap-1">
          {passes.map((credential) => (
            <li
              key={credential.id}
              className="flex flex-wrap gap-3 text-sm"
              data-testid="credential-row"
            >
              <span>{t(`person.record.credential_${credential.status}`)}</span>
              <span data-numeric className="text-muted-foreground">
                {format.dateTime(credential.issuedAt)}
              </span>
            </li>
          ))}
        </ul>
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-sm font-semibold">{t('person.record.revisions')}</h3>
        <ul className="flex flex-col gap-1 text-sm">
          {(revisions.data?.items ?? []).map((revision) => (
            <li key={revision.id} className="flex flex-wrap gap-3" data-testid="revision-row">
              <span data-numeric>{format.integer(revision.revision)}</span>
              <span data-numeric className="text-muted-foreground">
                {format.dateTime(revision.createdAt)}
              </span>
              <span className="font-mono text-xs text-muted-foreground">
                {revision.changedFieldKeys.join(' ')}
              </span>
            </li>
          ))}
        </ul>
      </section>

      {erase.error != null && <FailureNote error={erase.error} />}

      <EraseDialog
        person={person}
        open={erasing}
        pending={erase.submitting}
        onConfirm={(reasonCode) => {
          erase.submit(reasonCode);
        }}
        onCancel={() => {
          setErasing(false);
        }}
      />
    </div>
  );
}
